import React from "react";
import Head from "next/head";
import styled from "@emotion/styled";

const HOMEPAGE_IMAGE_PATH = "/images/app/zero/hero/homepage.webp";

type ZeroHeroProps = {
  title: string;
  subtitle: string;
  call_to_action_text: string;
  call_to_action_link: string;
};

export const ZeroHero = ({
  title,
  subtitle,
  call_to_action_link,
  call_to_action_text,
}: ZeroHeroProps) => {
  return (
    <>
      <Head>
        <link
          rel="preload"
          fetchPriority="high"
          as="image"
          href={HOMEPAGE_IMAGE_PATH}
        />
      </Head>
      <Wrapper>
        <InnerWrapper>
          <ContentWrapper>
            <TextWrapper>
              <Title>{title}</Title>
              <Subtitle>{subtitle}</Subtitle>
            </TextWrapper>
            <CallToAction href={call_to_action_link}>
              {call_to_action_text}
            </CallToAction>
          </ContentWrapper>
          <HomepageImage
            src={HOMEPAGE_IMAGE_PATH}
            alt="Ecologi Zero Homepage"
          />
        </InnerWrapper>
      </Wrapper>
    </>
  );
};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  padding: 180px 32px;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.teal85};
  overflow: hidden;

  @media ${(props) => props.theme.bp.desktopUp} {
    min-height: 801px;
    padding: 152px 40px 0px 40px;
  }

  @media ${(props) => props.theme.bp.veryWideDesktopUp} {
    padding: 152px 80px 0px 80px;
  }
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 93px;

  @media ${(props) => props.theme.bp.veryWideDesktopUp} {
    flex-direction: row;
    gap: 120px;
    max-width: 1600px;
    margin: 0 auto;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 40px;
  padding: 0px 8px;

  @media ${(props) => props.theme.bp.desktopUp} {
    width: 50%;
    padding: 48px 40px 0px 0px;
  }

  @media ${(props) => props.theme.bp.veryWideDesktopUp} {
    padding: 48px 0px 0px 0px;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;

  @media ${(props) => props.theme.bp.veryWideDesktopUp} {
    max-width: 630px;
  }
`;

const Title = styled.span`
  font-size: 48px;
  font-weight: 700;
  line-height: 56px;
  color: ${({ theme }) => theme.colors.snowdrop};

  @media ${(props) => props.theme.bp.desktopUp} {
    font-size: 56px;
    line-height: 64px;
  }
`;

const Subtitle = styled.span`
  font-size: 24px;
  line-height: 36px;
  color: ${({ theme }) => theme.colors.snowdrop};
`;

const CallToAction = styled.a`
  padding: 16px 28px 16px 24px;
  border-radius: 100px;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  text-decoration: none;
  background-color: ${({ theme }) => theme.colors.teal10};
  color: ${({ theme }) => theme.colors.teal80};

  &:hover {
    background-color: ${({ theme }) => theme.colors.forest5};
  }

  &:focus {
    outline: none;
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.teal10},
      inset 0 0 0 4px ${({ theme }) => theme.colors.teal80};
  }
`;

const HomepageImage = styled.img`
  width: calc(100% + 122px);
  margin-right: -122px;
  margin-bottom: -180px;

  @media ${(props) => props.theme.bp.desktopUp} {
    width: inherit;
    margin-bottom: inherit;
    margin-right: inherit;
    height: 656px;
    position: absolute;
    left: 50%;
    bottom: 0px;
  }

  @media ${(props) => props.theme.bp.veryWideDesktopUp} {
    position: inherit;
    right: inherit;
    bottom: inherit;
  }
`;
